// Import required libraries
import * as XLSX from 'xlsx';
import { CarrierWeightPricing } from '../../../../models/entities/admin/carrierWeightPricing';

// Define the region enums (copied from your provided code)
export enum RegionString {
    EUROPE = 'Europe',
    WESTERN_EUROPE = 'Western Europe',
    CHINA_HKG = 'China & Hong Kong',
    SINGAPORE = 'Singapore',
    JAPAN = 'Japan',
    INDIA = 'India',
    VIETNAM = 'Vietnam',
    THAILAND = 'Thailand',
    FAR_EAST_OTHERS = 'Far East Others',
    AUSTRALIA = 'Australia',
    NEW_ZEALAND = 'New Zealand',
    BAHRAIN = 'Bahrain',
    SOUTH_AFRICA = 'South Africa',
    LATIN_AMERICA = 'Latin America',
    ROW = 'Rest of World'
}

export enum RegionNumber {
    EUROPE = 0,
    WESTERN_EUROPE = 1,
    CHINA_HKG = 2,
    SINGAPORE = 3,
    JAPAN = 4,
    INDIA = 5,
    VIETNAM = 6,
    THAILAND = 7,
    FAR_EAST_OTHERS = 8,
    AUSTRALIA = 9,
    NEW_ZEALAND = 10,
    BAHRAIN = 11,
    SOUTH_AFRICA = 12,
    LATIN_AMERICA = 13,
    ROW = 14
}

export class Region {
    static stringToNumber(region: RegionString): RegionNumber {
        switch (region) {
            case RegionString.EUROPE:
                return RegionNumber.EUROPE;
            case RegionString.WESTERN_EUROPE:
                return RegionNumber.WESTERN_EUROPE;
            case RegionString.CHINA_HKG:
                return RegionNumber.CHINA_HKG;
            case RegionString.SINGAPORE:
                return RegionNumber.SINGAPORE;
            case RegionString.JAPAN:
                return RegionNumber.JAPAN;
            case RegionString.INDIA:
                return RegionNumber.INDIA;
            case RegionString.VIETNAM:
                return RegionNumber.VIETNAM;
            case RegionString.THAILAND:
                return RegionNumber.THAILAND;
            case RegionString.FAR_EAST_OTHERS:
                return RegionNumber.FAR_EAST_OTHERS;
            case RegionString.AUSTRALIA:
                return RegionNumber.AUSTRALIA;
            case RegionString.NEW_ZEALAND:
                return RegionNumber.NEW_ZEALAND;
            case RegionString.BAHRAIN:
                return RegionNumber.BAHRAIN;
            case RegionString.SOUTH_AFRICA:
                return RegionNumber.SOUTH_AFRICA;
            case RegionString.LATIN_AMERICA:
                return RegionNumber.LATIN_AMERICA;
            case RegionString.ROW:
                return RegionNumber.ROW;
        }
    }

    static numberToString(region: RegionNumber): RegionString {
        switch (region) {
            case RegionNumber.EUROPE:
                return RegionString.EUROPE;
            case RegionNumber.WESTERN_EUROPE:
                return RegionString.WESTERN_EUROPE;
            case RegionNumber.CHINA_HKG:
                return RegionString.CHINA_HKG;
            case RegionNumber.SINGAPORE:
                return RegionString.SINGAPORE;
            case RegionNumber.JAPAN:
                return RegionString.JAPAN;
            case RegionNumber.INDIA:
                return RegionString.INDIA;
            case RegionNumber.VIETNAM:
                return RegionString.VIETNAM;
            case RegionNumber.THAILAND:
                return RegionString.THAILAND;
            case RegionNumber.FAR_EAST_OTHERS:
                return RegionString.FAR_EAST_OTHERS;
            case RegionNumber.AUSTRALIA:
                return RegionString.AUSTRALIA;
            case RegionNumber.NEW_ZEALAND:
                return RegionString.NEW_ZEALAND;
            case RegionNumber.BAHRAIN:
                return RegionString.BAHRAIN;
            case RegionNumber.SOUTH_AFRICA:
                return RegionString.SOUTH_AFRICA;
            case RegionNumber.LATIN_AMERICA:
                return RegionString.LATIN_AMERICA;
            case RegionNumber.ROW:
                return RegionString.ROW;
        }
    }
}

// Define types for our parser
interface RegionColumn {
    originalHeader: string;
    regions: {
        enumStringValue: RegionString;
        enumNumberValue: RegionNumber;
    }[];
}

interface SheetData {
    columns: RegionColumn[];
    data: any[][];
}

interface ParsedData {
    [sheetName: string]: SheetData;
}

// Define the TNTExcelParser class
class TNTExcelParser {
    // The Excel workbook
    private workbook: XLSX.WorkBook;

    /**
     * Constructor
     * @param workbook The Excel workbook to parse
     */
    constructor(workbook: XLSX.WorkBook) {
        this.workbook = workbook;
    }

    // Map of Excel column headers to regions
    private headerToRegionMap: { [key: string]: RegionString[] } = {
        // TNT EXPRESS - IMPORT
        Europe: [RegionString.EUROPE],
        'China & HKG': [RegionString.CHINA_HKG],
        'Singapore, Japan': [RegionString.SINGAPORE, RegionString.JAPAN],
        India: [RegionString.INDIA],
        Vietnam: [RegionString.VIETNAM],
        Thailand: [RegionString.THAILAND],
        'Far East Others': [RegionString.FAR_EAST_OTHERS],
        'Australia, N. Zealand, Bahrain, Sth Africa & Latin Am.': [
            RegionString.AUSTRALIA,
            RegionString.NEW_ZEALAND,
            RegionString.BAHRAIN,
            RegionString.SOUTH_AFRICA,
            RegionString.LATIN_AMERICA
        ],
        ROW: [RegionString.ROW],

        // TNT EXPRESS - EXPORT
        'China & Hong Kong': [RegionString.CHINA_HKG],
        'India Singapore Japan': [RegionString.INDIA, RegionString.SINGAPORE, RegionString.JAPAN],
        'Australia N. Zealand Bahrain Sth Africa': [
            RegionString.AUSTRALIA,
            RegionString.NEW_ZEALAND,
            RegionString.BAHRAIN,
            RegionString.SOUTH_AFRICA
        ],

        // TNT ECONOMY - EXPORT
        'Western Europe': [RegionString.WESTERN_EUROPE]
    };

    // Expected sheet names
    private expectedSheets = ['יבוא  - TNT EXPRESS', 'יבוא - TNT ECONOMY', 'יצוא - TNT EXPRESS', 'יצוא - TNT ECONOMY    '];

    /**
     * Parse an Excel file and extract region data according to our defined enums
     * @param fileData The Excel file binary data
     * @returns Structured data with regions mapped to enums
     */
    async parseExcelFile(workbook: XLSX.WorkBook): Promise<ParsedData> {
        try {
            const result: ParsedData = {};

            // Process each sheet
            for (const sheetName of this.expectedSheets) {
                if (workbook.SheetNames.includes(sheetName)) {
                    result[sheetName] = this.processSheet(workbook.Sheets[sheetName]);
                }
            }

            return result;
        } catch (error) {
            console.error('Error parsing Excel file:', error);
            throw new Error(`Failed to parse Excel file: ${error}`);
        }
    }

    /**
     * Process a single sheet from the workbook
     * @param sheet The Excel sheet
     * @returns Structured data for the sheet
     */
    private processSheet(sheet: XLSX.WorkSheet): SheetData {
        // Convert sheet to JSON with headers
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (jsonData.length === 0) {
            return { columns: [], data: [] };
        }

        // Extract headers from the first row
        const headers = jsonData[0] as string[];

        // Map headers to regions
        const columns: RegionColumn[] = headers.map((header) => {
            const regions = this.mapHeaderToRegions(header);
            return {
                originalHeader: header,
                regions: regions.map((region) => ({
                    enumStringValue: region,
                    enumNumberValue: Region.stringToNumber(region)
                }))
            };
        });

        // Extract data rows (skip header row)
        const data = jsonData.slice(1) as any[][];

        return { columns, data };
    }

    /**
     * Map an Excel header to one or more regions
     * @param header The Excel column header
     * @returns Array of mapped regions
     */
    private mapHeaderToRegions(header: string): RegionString[] {
        // Check if we have a direct mapping
        if (this.headerToRegionMap[header]) {
            return this.headerToRegionMap[header];
        }

        // If not found, try to intelligently parse the header
        // This is a fallback mechanism for headers we didn't explicitly define

        // Try exact matches with enum values first
        const enumValues = Object.values(RegionString);
        for (const enumValue of enumValues) {
            if (header.includes(enumValue)) {
                return [enumValue as RegionString];
            }
        }

        // For unknown headers, log a warning and return an empty array
        console.warn(`Unknown header "${header}" not mapped to any region enum`);
        return [];
    }

    /**
     * Get a specific region's data from the parsed result
     * @param parsedData The parsed Excel data
     * @param sheetName The sheet name
     * @param region The region enum to find
     * @returns Data for the specified region
     */
    getRegionData(parsedData: ParsedData, sheetName: string, region: RegionString): number[] {
        if (!parsedData[sheetName]) {
            throw new Error(`Sheet "${sheetName}" not found in parsed data`);
        }

        const sheet = parsedData[sheetName];

        // Find column indices that contain the specified region
        const columnIndices: number[] = [];

        sheet.columns.forEach((column, index) => {
            if (column.regions.some((r) => r.enumStringValue === region)) {
                columnIndices.push(index);
            }
        });

        if (columnIndices.length === 0) {
            throw new Error(`Region "${region}" not found in sheet "${sheetName}"`);
        }

        // Extract data for the found columns
        return sheet.data.map((row) => {
            // For simplicity, we'll take the first matching column if multiple are found
            return row[columnIndices[0]];
        });
    }

    /**
     * Get all data for a specific region across all sheets
     * @param parsedData The parsed Excel data
     * @param region The region to find
     * @returns Data for the region across all sheets
     */
    getAllRegionData(parsedData: ParsedData, region: RegionString): { [sheetName: string]: number[] } {
        const result: { [sheetName: string]: number[] } = {};

        for (const sheetName of Object.keys(parsedData)) {
            try {
                const data = this.getRegionData(parsedData, sheetName, region);
                result[sheetName] = data;
            } catch (error) {
                // Region not found in this sheet, skip
            }
        }

        return result;
    }

    /**
     * Helper method to get all parsed regions
     * @param parsedData The parsed Excel data
     * @returns All unique regions found in the data
     */
    getAllParsedRegions(parsedData: ParsedData): RegionString[] {
        const regions = new Set<RegionString>();

        Object.values(parsedData).forEach((sheet) => {
            sheet.columns.forEach((column) => {
                column.regions.forEach((region) => {
                    regions.add(region.enumStringValue);
                });
            });
        });

        return Array.from(regions);
    }

    /**
     * Parse a weight value from the Excel cell
     * @param cellValue The value from the cell
     * @returns The parsed weight as a number or null if invalid
     */
    private parseWeight(cellValue: any): number | null {
        if (cellValue === undefined || cellValue === null || cellValue === '') {
            return null;
        }

        // Handle numeric values directly
        if (typeof cellValue === 'number') {
            return cellValue;
        }

        // Convert to string for text processing
        const value = String(cellValue).trim();

        // Handle special formats like "101+", "101>", "250>"
        if (value.endsWith('+') || value.endsWith('>')) {
            // Extract the numeric part
            const numericPart = value.replace(/[+>]/g, '');
            const parsedValue = parseFloat(numericPart);

            if (!isNaN(parsedValue)) {
                return parsedValue;
            }
        }

        // Try direct numeric parsing
        const parsedValue = parseFloat(value);
        return !isNaN(parsedValue) ? parsedValue : null;
    }

    /**
     * Parse a price value from the Excel cell
     * @param cellValue The value from the cell
     * @returns The parsed price as a number or null if invalid
     */
    private parsePrice(cellValue: any): number | null {
        if (cellValue === undefined || cellValue === null || cellValue === '') {
            return null;
        }

        // Handle numeric values directly
        if (typeof cellValue === 'number') {
            return cellValue;
        }

        // Convert to string and remove any non-numeric characters except decimal point
        const value = String(cellValue)
            .trim()
            .replace(/[^\d.]/g, '');

        // Parse as float
        const parsedValue = parseFloat(value);
        return !isNaN(parsedValue) ? parsedValue : null;
    }

    /**
     * Parse workbooks to extract all pricing data, grouped by sheet and region
     * @returns Object containing pricing data grouped by sheets and regions
     */
    public parseWorkbooks(): {
        bySheet: { [sheetName: string]: CarrierWeightPricing[] };
        byRegion: { [regionId: number]: CarrierWeightPricing[] };
        allPricing: CarrierWeightPricing[];
    } {
        try {
            // Get sheet names
            const sheets = {
                tntExpressImport: {
                    name: this.workbook.SheetNames[0],
                    carrierCode: '1000',
                    type: 'IMPORT' as const,
                    extraWeights: [
                        { rawText: '> 101', weight: 101 },
                        { rawText: '> 250', weight: 250 }
                    ]
                },
                tntEconomyImport: {
                    name: this.workbook.SheetNames[1],
                    carrierCode: '1001',
                    type: 'IMPORT' as const,
                    extraWeights: [{ rawText: '101+', weight: 101 }]
                },
                tntExpressExport: {
                    name: this.workbook.SheetNames[2],
                    carrierCode: '1000',
                    type: 'EXPORT' as const,
                    extraWeights: [{ rawText: '> 101', weight: 101 }]
                },
                tntEconomyExport: {
                    name: this.workbook.SheetNames[3],
                    carrierCode: '1001',
                    type: 'EXPORT' as const,
                    extraWeights: [
                        { rawText: '> 101', weight: 101 },
                        { rawText: '>250', weight: 250 }
                    ]
                }
            };

            // Process each sheet
            const result = {
                bySheet: {} as { [sheetName: string]: CarrierWeightPricing[] },
                byRegion: {} as { [regionId: number]: CarrierWeightPricing[] },
                allPricing: [] as CarrierWeightPricing[]
            };

            // Process each sheet
            for (const [key, sheet] of Object.entries(sheets)) {
                const sheetData = this.getSheetData(this.workbook.Sheets[sheet.name], sheet.carrierCode, sheet.type, sheet.extraWeights);

                // Store data by sheet
                result.bySheet[key] = sheetData;

                // Add to all pricing
                result.allPricing.push(...sheetData);

                // Group by region
                sheetData.forEach((pricing) => {
                    if (!result.byRegion[pricing.region]) {
                        result.byRegion[pricing.region] = [];
                    }
                    result.byRegion[pricing.region].push(pricing);
                });
            }

            return result;
        } catch (error) {
            console.error('Error parsing Excel workbooks:', error);
            throw new Error(`Failed to parse Excel workbooks: ${error}`);
        }
    }

    /**
     * Process a single sheet to extract pricing data
     * @param worksheet The Excel worksheet
     * @param carrierCode The carrier code
     * @param type Import or export type
     * @param extraWeights Special weight patterns to handle
     * @returns Array of CarrierWeightPricing objects
     */
    private getSheetData(
        worksheet: XLSX.WorkSheet,
        carrierCode: string,
        type: 'IMPORT' | 'EXPORT',
        extraWeights: { rawText: string; weight: number }[] = []
    ): CarrierWeightPricing[] {
        // Convert sheet to JSON with headers
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        if (data.length < 2) return []; // Need at least headers and one row of data

        // Extract headers and body
        const dataHeader: any = data[0];
        const dataBody = data.slice(1);
        const pricing: CarrierWeightPricing[] = [];

        // Process each row and build structured data
        const jsonData = dataBody.map((row: any) => {
            const obj: {
                weight: string;
                regionPricing: { regionId: number; price: number }[];
            } = { weight: '', regionPricing: [] };

            // Process each cell in the row
            row.forEach((cell: any, index: number) => {
                const header = dataHeader[index];
                if (header) {
                    // Check if this column maps to multiple regions
                    const regions = this.mapHeaderToRegions(header.trim());

                    if (regions.length > 0) {
                        // This cell could map to multiple regions
                        const price = this.parsePrice(cell);
                        if (price !== null && typeof price === 'number') {
                            // Add an entry for each region this header maps to
                            regions.forEach((region) => {
                                const regionId = Region.stringToNumber(region);
                                obj.regionPricing.push({
                                    regionId,
                                    price: this.roundPrice(price)
                                });
                            });
                        }
                    } else if (header === 'KG' || index === 0) {
                        // This is a weight column
                        obj.weight = cell + '';
                    }
                }
            });

            return obj;
        });

        // Filter out rows with weight over 100 (unless they match special patterns)
        const filteredJsonData = jsonData.filter((row) => {
            const numWeight = parseFloat(row.weight);
            return !isNaN(numWeight) && numWeight < 101;
        });

        // Handle special weight patterns (like "101+", "> 250", etc.)
        let additionalData = jsonData.filter((row) => extraWeights.some((ew) => row.weight.includes(ew.rawText)));

        // Map special weight patterns to their actual weight values
        additionalData = additionalData.map((row) => {
            const weight = extraWeights.find((ew) => row.weight.includes(ew.rawText));
            if (weight) {
                row.weight = weight.weight + '';
            }
            return row;
        });

        // Combine regular and special weight data
        filteredJsonData.push(...additionalData);

        // Create final pricing objects
        filteredJsonData.forEach((row) => {
            // Process each region pricing entry
            row.regionPricing.forEach((regionPricing: { regionId: number; price: number }) => {
                // Skip invalid prices
                if (regionPricing.price === null || isNaN(regionPricing.price)) {
                    return;
                }

                const carrierWeightPricing: CarrierWeightPricing = {
                    carrierCode,
                    region: regionPricing.regionId,
                    weight: +row.weight,
                    price: regionPricing.price,
                    type
                };
                pricing.push(carrierWeightPricing);
            });
        });

        return pricing;
    }

    /**
     * Get region ID from header text
     * @param header The header text
     * @returns The region ID or null if not found
     */
    private getRegionIdFromHeader(header: string): number | null {
        // Try to map header to regions
        const regions = this.mapHeaderToRegions(header);

        if (regions.length > 0) {
            // Return the numeric enum value of the first region
            return Region.stringToNumber(regions[0]);
        }

        return null;
    }

    /**
     * Round price to 2 decimal places
     * @param price The price to round
     * @returns Rounded price
     */
    private roundPrice(price: number): number {
        return Math.round(price * 100) / 100;
    }
}

export { TNTExcelParser };
